<template>
  <div v-if="event">
    <div class="animate__animated animate__fadeIn">
      <div class="row mb-3">
        <div class="col-md-8 col-12">
          <h3 class="mb-2">{{ event.title }}</h3>
          <h5 class="fs-6">{{ event.description }}</h5>
          <br />
          <div class="mb-2">
            <span class="badge bg-secondary fs-6">
              {{ event.modality }}
            </span>
          </div>
        </div>
        <div class="col-md-4 col-12">
          <CardNumber
            :title="etapaTitle"
            :value="etapaValue"
            color="info"
            :subtitle="etapaSubtitle"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <CardNumber
            title="Total Participantes"
            :value="participantsCount"
            color="primary"
            :subtitle="'Participantes registrados'"
          />
        </div>
        <div class="col-md-4">
          <CardNumber
            title="Asistencia"
            :value="attendanceCount"
            color="success"
            :subtitle="`De ${participantsCount} participantes`"
          />
        </div>
        <div
          class="col-md-4 d-flex flex-column align-items-center justify-content-center"
        >
          <template v-if="event.state === 2">
            <div v-if="event.attendance_code" class="text-center mb-3">
              <CardNumber
                title="Código de Asistencia"
                :value="event.attendance_code"
                color="success"
                subtitle="Compártelo para que los participantes puedan registrar su asistencia"
              />
              <button
                class="btn btn-primary mt-2"
                @click="copyToClipboard(event.attendance_code)"
              >
                Copiar Código
              </button>
            </div>

            <button
              class="btn btn-danger"
              @click="concludeEvent"
              :disabled="!canConcludeEvent || event.state === 3"
            >
              Concluir Evento
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import CardNumber from "../components/cardNumber.vue";
import axios from "@/includes/connection";
import { inject } from "vue";
import { toast } from "vue3-toastify";

const swal = inject("$swal");
const props = defineProps({
  event: Object,
});

// Estados reactivos
const attendanceCount = ref(0);
const participantsCount = ref(0);

function formatDate(dateString) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("fr-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function badgeClass(state) {
  if (state === 2) return "bg-success me-2 mb-3 fs-6";
  if (state === 3) return "bg-danger me-2 mb-3 fs-6";
  return "bg-secondary";
}

const canConcludeEvent = computed(() => {
  const now = new Date();
  const eventStart = new Date(props.event.init_date);
  return now >= eventStart;
});

function isSameDay(dateA, dateB) {
  const a = new Date(dateA);
  const b = new Date(dateB);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const etapaTitle = computed(() => "ETAPA");

const etapaSubtitle = computed(() => {
  const now = new Date();
  const inscStart = new Date(props.event.init_date_inscription);
  const inscEnd = new Date(props.event.end_date_inscription);
  const eventStart = new Date(props.event.init_date);
  const eventEnd = new Date(props.event.end_date).setHours(23, 59, 59, 0);

  if (props.event.state === 3) {
    if (props.event.updated_at) {
      return `Concluido\n${formatDate(props.event.updated_at)}`;
    } else {
      return "Concluido";
    }
  }

  if (now >= inscStart && now <= inscEnd) {
    if (
      isSameDay(
        props.event.init_date_inscription,
        props.event.end_date_inscription
      )
    ) {
      return `Inscripciones\n${formatDate(props.event.init_date_inscription)}`;
    } else {
      return `Inscripciones\n${formatDate(
        props.event.init_date_inscription
      )} - ${formatDate(props.event.end_date_inscription)}`;
    }
  }

  if (now >= eventStart && now <= eventEnd) {
    if (isSameDay(props.event.init_date, props.event.end_date)) {
      return `En Curso`;
    } else {
      return `En Curso\n${formatDate(props.event.init_date)} - ${formatDate(
        props.event.end_date
      )}`;
    }
  }

  if (props.event.state === 3) {
    if (props.event.updated_at) {
      return `Concluido\n${formatDate(props.event.updated_at)}`;
    } else {
      return "Concluido";
    }
  }

  if (isSameDay(props.event.init_date, props.event.end_date)) {
    return `Comienza a las\n${props.event.hour}`;
  } else {
    return `\n${formatDate(props.event.init_date)} - ${formatDate(
      props.event.end_date
    )}`;
  }
});

const etapaValue = computed(() => "");

async function fetchAttendance() {
  if (!props.event?.id) return;

  try {
    const { data } = await axios.get(`/attendance/${props.event.id}`);
    participantsCount.value = data.length;
    attendanceCount.value = data.filter((p) => p.is_attendance).length;
  } catch (e) {
    console.error("Error fetching attendance:", e);
    participantsCount.value = 0;
    attendanceCount.value = 0;
  }
}

function copyToClipboard(text) {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.success("Código copiado al portapapeles", {
        autoClose: 2000,
        position: "bottom-right",
      });
    })
    .catch(() => {
      toast.error("No se pudo copiar el código", {
        autoClose: 3000,
        position: "bottom-right",
      });
    });
}

async function concludeEvent() {
  swal
    .fire({
      title: "¿Está seguro?",
      text: "Esta acción concluirá el evento y no podrá modificarlo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sí, concluir",
      focusCancel: true,
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.patch(`/event/${props.event.id}/conclude`);
          swal.fire({
            icon: "success",
            title: "Evento concluido",
            text: "El evento se concluyó correctamente.",
          });
          props.event.state = 3;
          await fetchAttendance();
        } catch (e) {
          if (e.response && e.response.status === 404) {
            swal.fire({
              icon: "error",
              title: "No encontrado",
              text: "Evento no encontrado.",
            });
          } else if (e.response && e.response.status === 400) {
            swal.fire({
              icon: "error",
              title: "Solicitud inválida",
              text: "No se puede concluir el evento.",
            });
          } else if (e.response && e.response.status === 401) {
            swal.fire({
              icon: "error",
              title: "No autorizado",
              text: "No tiene permisos para concluir el evento.",
            });
          } else {
            swal.fire({
              icon: "error",
              title: "Error",
              text: "Ocurrió un error al concluir el evento.",
            });
          }
        }
      }
    });
}

watch(
  () => props.event,
  (newEvent) => {
    if (newEvent) {
      fetchAttendance();
    }
  },
  { immediate: true }
);

onMounted(() => {
  fetchAttendance();
});
</script>

<style scoped></style>
